import { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

// master_data only stores each city's current official name (e.g. "Bengaluru"),
// but plenty of people still type the pre-rename name daily. Without this, the
// search box looks like a whole state's cities are missing (reported: typing
// "Banglore"/"Bangalore" showed no Karnataka cities at all — 2026-09-15).
const CITY_ALIASES = {
  bengaluru: ['bangalore', 'banglore'],
  mumbai: ['bombay'],
  kolkata: ['calcutta'],
  chennai: ['madras'],
  pune: ['poona'],
  vadodara: ['baroda'],
  kochi: ['cochin'],
  mysuru: ['mysore'],
  thiruvananthapuram: ['trivandrum'],
  gurugram: ['gurgaon'],
  prayagraj: ['allahabad'],
  kanpur: ['cawnpore'],
  visakhapatnam: ['vizag', 'vishakhapatnam'],
  belagavi: ['belgaum'],
  mangaluru: ['mangalore'],
  hubballi: ['hubli'],
  puducherry: ['pondicherry'],
};
const cityMatchesQuery = (item, lowerQuery) => {
  const value = item.value.toLowerCase();
  if (value.includes(lowerQuery)) return true;
  return (CITY_ALIASES[value] || []).some(alias => alias.includes(lowerQuery));
};

/**
 * CityStateSelect
 * A searchable city dropdown that:
 *   - Filters 455 Indian cities as user types
 *   - Shows parent state as subtitle for disambiguation
 *   - Auto-fills the paired state field when a city is chosen
 *   - Offers "+ Add '[typed]' as new city" when no match found
 *
 * Props:
 *   city        string    — controlled city value
 *   state       string    — controlled state value
 *   onCityChange  fn(city)
 *   onStateChange fn(state)
 *   stateOptions  string[]  — list of state names for the state dropdown
 *   disabled    bool
 */
export function CityStateSelect({ city, state, onCityChange, onStateChange, stateOptions = [], disabled = false, stateRequired = false }) {
  const [cityItems, setCityItems]   = useState([]);
  const [query, setQuery]           = useState('');
  const [open, setOpen]             = useState(false);
  const [adding, setAdding]         = useState(false);
  const [addExpanded, setAddExpanded] = useState(false);
  const [pendingState, setPendingState] = useState('');
  const cityRef                     = useRef(null);
  const stateRef                    = useRef(null);
  const [stateOpen, setStateOpen]   = useState(false);

  // Load city master data once
  useEffect(() => {
    api.get('/master-data/city')
      .then(r => setCityItems(r.data.data || []))
      .catch(() => {});
  }, []);

  // Sync query display when value is set externally (e.g. prefill from prospect)
  // Also auto-fill state if city matches a known city and state is not yet set
  useEffect(() => {
    if (city && query !== city) {
      setQuery(city);
      // If state not already set and cityItems loaded, auto-resolve state from city
      if (!state && cityItems.length > 0) {
        const match = cityItems.find(c => c.value.toLowerCase() === city.toLowerCase());
        if (match?.description) onStateChange(match.description);
      }
    }
  }, [city, cityItems]); // intentionally omits query/state/onStateChange to avoid loop

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = e => {
      if (cityRef.current && !cityRef.current.contains(e.target)) setOpen(false);
      if (stateRef.current && !stateRef.current.contains(e.target)) setStateOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? cityItems.filter(c => cityMatchesQuery(c, query.toLowerCase())).slice(0, 40)
    : cityItems.slice(0, 40);

  const exactMatch = cityItems.some(c => c.value.toLowerCase() === query.trim().toLowerCase());
  const showAddOption = query.trim().length > 1 && !exactMatch;

  const selectCity = useCallback((item) => {
    onCityChange(item.value);
    setQuery(item.value);
    if (item.description) onStateChange(item.description);
    setOpen(false);
  }, [onCityChange, onStateChange]);

  const handleAddCity = async (chosenState) => {
    const newCity = query.trim();
    if (!newCity) return;
    setAdding(true);
    try {
      const r = await api.post('/master-data', {
        category: 'city',
        value: newCity,
        label: newCity,
        description: chosenState || state || null,
      });
      const created = r.data.data;
      setCityItems(prev => [...prev, created]);
      onCityChange(created.value);
      setQuery(created.value);
      if (created.description) onStateChange(created.description);
      setOpen(false);
      setAddExpanded(false);
      setPendingState('');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save this city to the shared list — using it for this record only');
      onCityChange(newCity);
      if (chosenState) onStateChange(chosenState);
      setOpen(false);
      setAddExpanded(false);
    } finally {
      setAdding(false);
    }
  };

  const clearCity = e => {
    e.stopPropagation();
    onCityChange('');
    setQuery('');
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

      {/* ── City field ── */}
      <div className="form-group" style={{ margin: 0 }} ref={cityRef}>
        <label className="form-label">City</label>
        <div style={{ position: 'relative' }}>
          <input
            className="form-control"
            value={query}
            placeholder="Search city…"
            disabled={disabled}
            onChange={e => { setQuery(e.target.value); onCityChange(''); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // On blur: if typed value matches a known city, accept it and auto-fill state
              const typed = query.trim();
              if (typed) {
                const match = cityItems.find(c => c.value.toLowerCase() === typed.toLowerCase());
                if (match) {
                  onCityChange(match.value);
                  setQuery(match.value);
                  if (match.description && !state) onStateChange(match.description);
                } else {
                  // Accept custom city as-is
                  onCityChange(typed);
                }
              }
              setTimeout(() => setOpen(false), 150);
            }}
            autoComplete="off"
          />
          {query && !disabled && (
            <span
              onClick={clearCity}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}
            >✕</span>
          )}
          {open && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
              marginTop: 3, maxHeight: 260, overflowY: 'auto',
            }}>
              {filtered.length === 0 && !showAddOption && (
                <div style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>No cities found</div>
              )}
              {filtered.map(item => (
                <div
                  key={item.id || item.value}
                  onMouseDown={() => selectCity(item)}
                  style={{
                    padding: '8px 14px', cursor: 'pointer',
                    background: item.value === city ? 'var(--brand-purple-faint)' : 'transparent',
                    borderBottom: '1px solid var(--border)',
                  }}
                  onMouseEnter={e => { if (item.value !== city) e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { if (item.value !== city) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ fontSize: '0.875rem', fontWeight: item.value === city ? 600 : 400, color: item.value === city ? 'var(--brand-purple)' : 'var(--text-primary)' }}>
                    {item.value}
                  </div>
                  {item.description && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>{item.description}</div>
                  )}
                </div>
              ))}
              {showAddOption && (
                <>
                  {filtered.length > 0 && <div style={{ borderTop: '1px solid var(--border)' }} />}
                  {!addExpanded ? (
                    <div
                      onMouseDown={e => { e.preventDefault(); setAddExpanded(true); setPendingState(state || ''); }}
                      style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--brand-purple)', fontSize: '0.85rem', fontWeight: 500 }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-purple-faint)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span>
                      {`Add "${query.trim()}" as new city`}
                    </div>
                  ) : (
                    <div style={{ padding: '10px 14px', background: 'var(--brand-purple-faint)' }} onMouseDown={e => e.preventDefault()}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--brand-purple)', marginBottom: 8 }}>
                        Add "{query.trim()}"
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>State *</label>
                        <select
                          className="form-control"
                          style={{ fontSize: '0.85rem', padding: '5px 8px', height: 'auto' }}
                          value={pendingState}
                          onChange={e => setPendingState(e.target.value)}
                          autoFocus
                        >
                          <option value="">Select state…</option>
                          {(stateOptions.length > 0 ? stateOptions : [
                            'Andhra Pradesh','Chhattisgarh','Delhi','Gujarat','Kerala',
                            'M H (Maharashtra)','Madhya Pradesh','Punjab','Rajasthan',
                            'Tamilnadu','Telangana','U P (Uttar Pradesh)',
                          ]).map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: '0.8rem', padding: '5px 14px' }}
                          disabled={adding || !pendingState}
                          title={!pendingState ? 'Select a state first' : undefined}
                          onClick={() => handleAddCity(pendingState)}
                        >
                          {adding ? 'Adding…' : 'Add City'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: '0.8rem', padding: '5px 10px' }}
                          onClick={() => { setAddExpanded(false); setPendingState(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── State field ── */}
      <div className="form-group" style={{ margin: 0 }} ref={stateRef}>
        <label className="form-label">State{stateRequired && <span style={{color:'var(--danger)',marginLeft:2}}>*</span>}</label>
        <div style={{ position: 'relative' }}>
          <div
            className="form-control"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1, userSelect: 'none',
              borderColor: stateOpen ? 'var(--brand-purple)' : undefined,
              boxShadow: stateOpen ? '0 0 0 3px rgba(64,62,201,0.12)' : undefined,
            }}
            onClick={() => !disabled && setStateOpen(o => !o)}
          >
            <span style={{ flex: 1, color: state ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {state || 'Select state…'}
            </span>
            {state && !disabled && (
              <span onClick={e => { e.stopPropagation(); onStateChange(''); }} style={{ color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>✕</span>
            )}
            <span style={{ color: 'var(--text-muted)', fontSize: 10, flexShrink: 0 }}>{stateOpen ? '▲' : '▼'}</span>
          </div>
          {stateOpen && stateOptions.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
              marginTop: 3, maxHeight: 260, overflowY: 'auto',
            }}>
              {stateOptions.map(s => (
                <div
                  key={s}
                  onMouseDown={() => { onStateChange(s); setStateOpen(false); }}
                  style={{
                    padding: '8px 14px', fontSize: '0.875rem', cursor: 'pointer',
                    background: s === state ? 'var(--brand-purple-faint)' : 'transparent',
                    color: s === state ? 'var(--brand-purple)' : 'var(--text-primary)',
                    fontWeight: s === state ? 600 : 400,
                    borderBottom: '1px solid var(--border)',
                  }}
                  onMouseEnter={e => { if (s !== state) e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { if (s !== state) e.currentTarget.style.background = 'transparent'; }}
                >
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
